/**
 * Player headshot URLs. Each platform owns its name → CDN id maps and how it loads them; this decides
 * which id a name resolves to.
 */

import { getParentLeague, type LeagueKey } from './league';
import { fuzzyMatchPlayer, normalizePlayerNameToMatch } from './playerNameUtils';

const HEADSHOT_CDN_BY_LEAGUE: Partial<Record<LeagueKey, string>> = {
  basketball_nba: 'https://cdn.nba.com/headshots/nba/latest/260x190',
  basketball_wnba: 'https://cdn.wnba.com/headshots/wnba/latest/260x190',
};

export type PlayerIdMapsByLeague = Partial<Record<LeagueKey, Record<string, number>>>;

function findPlayerId(playerName: string, idMap: Record<string, number>): number | undefined {
  const exact = idMap[playerName] ?? idMap[normalizePlayerNameToMatch(playerName, true)];
  if (exact) return exact;
  for (const [name, id] of Object.entries(idMap)) {
    if (fuzzyMatchPlayer(playerName, name)) return id;
  }
  return undefined;
}

/**
 * The player's headshot from their own league's map only (NBA variants use the NBA map): exact name,
 * normalized name, then fuzzy. Null when the league has no headshot CDN or map, or the name is not in it.
 */
export function findPlayerHeadshotUrl(
  playerName: string,
  leagueKey: LeagueKey,
  idMapsByLeague: PlayerIdMapsByLeague,
): string | null {
  const headshotLeague = getParentLeague(leagueKey);
  const cdn = HEADSHOT_CDN_BY_LEAGUE[headshotLeague];
  const idMap = idMapsByLeague[headshotLeague];
  if (!cdn || !idMap || !playerName) return null;
  const playerId = findPlayerId(playerName, idMap);
  return playerId ? `${cdn}/${playerId}.png` : null;
}

/** Initials image for a player with no headshot. */
export function playerInitialsAvatarUrl(playerName: string): string {
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(playerName)}&background=3b82f6&color=fff&size=80&bold=true`;
}
